import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { FormField, MessageBanner, ModalActions, SelectInput, TextInput } from "@/components/ui/primitives";
import { localTodayYMD } from "@/utils/dateUtils";

export interface QuickTransactionData {
  name: string;
  amount: string;
  date: string;
  category: string;
  account: string;
  type: "EXPENSE";
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: QuickTransactionData) => void | Promise<void>;
  frequentAccounts: Record<string, number>;
  frequentCategories: Record<string, number>;
  fallbackAccounts: string[];
  fallbackCategories: string[];
}

const initialData = (): QuickTransactionData => ({ name: "", amount: "", date: localTodayYMD(), category: "", account: "", type: "EXPENSE" });

export const QuickTransactionModal = ({ isOpen, onClose, onSubmit, frequentAccounts, frequentCategories, fallbackAccounts, fallbackCategories }: Props) => {
  const { t } = useTranslation(["transactionModal", "common"]);
  const [formData, setFormData] = useState<QuickTransactionData>(initialData);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const accounts = useMemo(() => (Object.keys(frequentAccounts).length ? Object.keys(frequentAccounts) : fallbackAccounts).slice(0, 3), [fallbackAccounts, frequentAccounts]);
  const categories = useMemo(() => (Object.keys(frequentCategories).length ? Object.keys(frequentCategories) : fallbackCategories).slice(0, 3), [fallbackCategories, frequentCategories]);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(initialData());
    setError(null);
    requestAnimationFrame(() => nameInputRef.current?.focus());
  }, [isOpen]);

  if (!isOpen) return null;

  const update = <K extends keyof QuickTransactionData>(key: K, value: QuickTransactionData[K]) => setFormData((current) => ({ ...current, [key]: value }));
  const isValid = Boolean(formData.name.trim() && Number(formData.amount) > 0 && formData.date && formData.account);
  const submit = async () => {
    if (!isValid) { setError(t("transactionModal.quickValidationError")); return; }
    setIsSubmitting(true); setError(null);
    try { await onSubmit(formData); onClose(); }
    catch { setError(t("transactionModal.quickSubmitError")); }
    finally { setIsSubmitting(false); }
  };
  return createPortal(
    <ModalShell isOpen={isOpen} onClose={onClose} labelledBy="quick-transaction-title" title={t("transactionModal.quickTitle")} subtitle={t("transactionModal.quickSubtitle")} mobileSheet variant="creation" disableClose={isSubmitting} onSubmit={(event) => { event.preventDefault(); void submit(); }} footer={<ModalActions cancelLabel={t("common.actions.cancel")} submitLabel={t("transactionModal.createQuick")} onCancel={onClose} isSubmitting={isSubmitting} isSubmitDisabled={!isValid} />}>
      <div className="app-creation-form app-creation-form-grid mx-auto w-full max-w-xl">
        {error ? <MessageBanner className="app-creation-form-message" tone="error" role="alert">{error}</MessageBanner> : null}
        <FormField label={t("common.labels.name")} htmlFor="quick-name" required>
          <TextInput ref={nameInputRef} id="quick-name" value={formData.name} onChange={(event) => update("name", event.target.value)} placeholder={t("transactionModal.quickNamePlaceholder")} disabled={isSubmitting} />
        </FormField>
        <FormField label={t("common.labels.amount")} htmlFor="quick-amount" required>
          <TextInput id="quick-amount" type="text" inputMode="decimal" value={formData.amount} onChange={(event) => update("amount", event.target.value)} placeholder={t("transactionModal.quickAmountPlaceholder")} leftAddon="$" inputClassName="tabular-nums" disabled={isSubmitting} />
        </FormField>
        <FormField label={t("common.labels.date")} htmlFor="quick-date" required>
          <TextInput id="quick-date" type="date" value={formData.date} onChange={(event) => update("date", event.target.value)} disabled={isSubmitting} />
        </FormField>
        <FormField label={t("common.labels.category")} htmlFor="quick-category">
          <SelectInput id="quick-category" value={formData.category} onChange={(event) => update("category", event.target.value)} disabled={isSubmitting}><option value="">{t("transactionModal.quickNoCategory")}</option>{categories.map((name) => <option key={name} value={name}>{name}</option>)}</SelectInput>
        </FormField>
        <FormField className="app-creation-form-full" label={t("common.labels.account")} htmlFor="quick-account" required>
          <SelectInput id="quick-account" value={formData.account} onChange={(event) => update("account", event.target.value)} disabled={isSubmitting}><option value="">{t("transactionModal.quickSelectAccount")}</option>{accounts.map((name) => <option key={name} value={name}>{name}</option>)}</SelectInput>
        </FormField>
      </div>
    </ModalShell>, document.body,
  );
};
